import { useQuery, useQueryClient } from '@tanstack/react-query';

import { defaultSettingsByLang } from '@/utils/settings';
import { useExposePageProps } from '@/hooks/useExposePageProps';

import { useListing } from './useListing';
import { getParentProjectQueryKey } from './queryKeys';
import { useSettings } from '../useSettings';
import { useSearchApi } from '../useSearchApi';
import { useExposeLanguageStore } from '../useExposeLanguageStore';

export const useParentProject = () => {
  const searchApi = useSearchApi();
  const queryClient = useQueryClient();
  const { pathLanguage } = useExposePageProps();
  const { currency, measurementSystem, language } = useSettings();
  const exposeLanguage = useExposeLanguageStore((state) => state.exposeLanguage ?? language);
  const {
    data: { listing },
  } = useListing();

  return useQuery({
    queryKey: getParentProjectQueryKey({ listingId: listing?.parentId ?? '', language: exposeLanguage, currency, measurementSystem }),
    queryFn: async () => {
      if (!listing?.parentId) throw new Error('Missing parent project id');

      const parentProjectResponse = await searchApi.getListingById(listing.parentId, {
        language: exposeLanguage,
        currency,
        measurementSystem,
      });

      if (parentProjectResponse.status === 'error') {
        throw parentProjectResponse.error;
      }

      return parentProjectResponse.data.listing;
    },
    refetchOnMount: false,
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      // Try to get data from server query key if client settings differ
      const serverQueryKey = getParentProjectQueryKey({
        listingId: listing?.parentId ?? '',
        language: pathLanguage,
        ...defaultSettingsByLang[pathLanguage],
      });
      return queryClient.getQueryData(serverQueryKey);
    },
    enabled: Boolean(listing?.isDevelopmentUnit && listing.parentId),
  });
};
